/**
 * The RBAC vocabulary — role names and permission keys — as RUNTIME VALUES.
 *
 * ===========================================================================
 * WHY THESE ARRAYS ARE DECLARED HERE AND NOT IMPORTED FROM `@crf/shared`
 * ===========================================================================
 * `@crf/shared` compiles to CommonJS, so importing a VALUE from it would drag a
 * CJS module into the browser bundle (see the header of `src/lib/permissions.ts`,
 * which is why that module is `import type` only). The house pattern — set by
 * `domain/countries.ts` and `domain/stages.ts` — is a local literal plus a unit
 * test that asserts equality with the shared package, where requiring CJS is
 * free. `rbac.test.ts` is that test; a key added to or removed from
 * `packages/shared/src/permissions.ts` fails this app's suite until this mirror
 * moves with it.
 *
 * The literals are typed against the SHARED types (`satisfies`), so a typo here
 * does not even compile — the test exists to catch missing/extra entries, which
 * a type cannot.
 */

import type { PermissionKey, RoleName } from '../lib/permissions';
import { groupPermissions } from './permission-catalog';
import type { PermissionGroup } from './permission-catalog';

/**
 * The picker's group shape, re-exported so the two admin screens and `rbac.test.ts`
 * keep importing it from the module they always did. It is DECLARED in
 * `permission-catalog.ts`, which is where the groups themselves live.
 */
export type { PermissionGroup } from './permission-catalog';

/**
 * Appendix A role ROWS, in table order — mirror of `ROLE_NAMES` in `@crf/shared`.
 *
 * SEVEN ROWS since the Tally Sales brief (§B4) added `Tally Sales`. Group Admin
 * is still NOT among them: it is the `is_group_admin` flag (`grp:true` on the
 * token), never a row, so it is not assignable from the role editor and must not
 * appear in this list. The admin UI grants it through the group-admin action, not
 * through a country×role assignment.
 *
 * `Tally Sales` IS assignable here, and deliberately so — B4 says it is held by
 * different people from the CRF coordinators and may be given in any country.
 *
 * ⚠ THIS LIST NO LONGER FEEDS THE ROLE PICKER — custom-roles design §1.6. The user
 * screens' `RoleAssignmentsEditor` reads `GET /roles` now, because a hardcoded array
 * cannot offer a role an administrator authored. What this list still does is the job
 * it was written for and the only one it can do honestly: `rbac.test.ts` holds it
 * against `@crf/shared`, so the seeded names cannot drift between the matrix, the
 * seeder and the clients. It is a CONTRACT GUARD, not a source of options.
 *
 * Coordinator is back after the close-out retired it in error — a real,
 * single-country operating role holding `amounts.view`, not an alias for Country
 * Manager. `rbac.test.ts` compares this list against `@crf/shared`'s, so the two
 * cannot drift; that test is what caught the omission here.
 */
export const ROLE_NAMES = [
  'Coordinator',
  'Country Manager',
  'Accounts',
  'Dev Head',
  'Developer',
  'Tech Support',
  'Tally Sales',
] as const satisfies readonly RoleName[];

/**
 * `roles.name` is `VARCHAR(40)` — mirror of `ROLE_NAME_MAX_LENGTH` in `@crf/shared`.
 *
 * A LOCAL LITERAL for the same CJS reason as the arrays above, and it earns its keep the
 * same way: `rbac.test.ts` holds it against the shared constant, so the field's
 * `maxLength` and the server's refusal can never drift apart. Without it the create form
 * would let an operator type a name the API then refuses with a validation error they
 * could have been shown before the round trip.
 */
export const ROLE_NAME_MAX_LENGTH = 40;

/**
 * A MIRROR of `@crf/shared`'s `PERMISSION_KEYS`, kept as a local literal.
 *
 * The duplication is deliberate: importing the shared array as a VALUE would pull
 * a CommonJS module into the browser bundle (see the header of
 * `src/lib/permissions.ts`), so the app may only `import type` from it.
 *
 * IT IS NOT UNGUARDED, and the guard has now earned its keep. `rbac.test.ts`
 * asserts this list equals the shared one in order — a test file is not bundled,
 * so it may import the value the app must not. When `work.files.manage` (ESC-19)
 * was added to the registry and not here, that test and the workspace typecheck
 * both went red in the same gate run. This comment is the reminder that the
 * mirror is a liability paid for by a test, not a fact anybody remembers.
 */
export const PERMISSION_KEYS = [
  'inquiries.view',
  'inquiries.create',
  'inquiries.edit',
  'inquiries.stage',
  'parties.view',
  'parties.create',
  'parties.link',
  'quotes.view',
  'quotes.create',
  'quotes.send',
  'quotes.confirm',
  'quotes.discount.request',
  'quotes.discount.approve',
  'so.view',
  'crf.view',
  'crf.create',
  'crf.edit',
  'work.assign',
  'work.update',
  'work.files.manage',
  'testing.record',
  'testing.return',
  'deploy.record',
  'signoff.developer',
  'signoff.project',
  'signoff.manage',
  'billing.view',
  'billing.record',
  'amounts.view',
  'reports.view',
  'reports.export',
  'users.manage',
  'config.manage',
  'config.global',
  'dashboard.group',
  'remote.access',
  // Migration 52 (v17) — field tracking. Same order as the shared registry; the
  // Rule-14 split is: `.view` keys see masked registers, `field.location.view`
  // alone sees OTHERS' raw coordinates (self always sees self).
  'field.attendance.record',
  'field.attendance.view',
  'field.visits.record',
  'field.visits.view',
  'field.sessions.self',
  'field.sessions.request',
  'field.location.view',
  // Migration 64 (v24 Phase B, ruling AM-2) — the payments-ledger WRITE key.
  // Deliberately no `payments.view`: ledger reads ride `crf.view`, figures
  // masked under `amounts.view` (Rule 14 keeps one choke point).
  'payments.record',
] as const satisfies readonly PermissionKey[];

/**
 * Granted by NO role template — reachable only via `grp:true` or an explicit
 * override. Mirror of `GRP_ONLY_PERMISSIONS`; the overrides editor marks these
 * "override-only" and, matching `d5-boundary.ts`, lets only a group admin GRANT
 * one (revoking stays open to anyone inside the country boundary).
 */
export const GRP_ONLY_PERMISSIONS = [
  'config.global',
  'dashboard.group',
  'remote.access',
] as const satisfies readonly PermissionKey[];

const GRP_ONLY: ReadonlySet<PermissionKey> = new Set(GRP_ONLY_PERMISSIONS);

/** Is this key granted by no role template (override-only)? */
export function isOverrideOnly(key: PermissionKey): boolean {
  return GRP_ONLY.has(key);
}

/* ─────────────────────────────────────────────────────── the grouped picker ── */

/**
 * The registry, grouped BY CONCERN for the pickers — `domain/permission-catalog.ts`
 * holds the groups, the human labels and the descriptions.
 *
 * ⚠ THE MECHANISM CHANGED HERE, AND THE OLD ARGUMENT DESERVES AN ANSWER RATHER THAN A
 * DELETION. This function used to derive groups from the PREFIX before the first dot,
 * against a `GROUP_LABELS` map declared in this file, and its comment gave the reason:
 * "a curated list would need a human decision every time the registry moved, and a
 * forgotten decision would silently drop a key from the picker". The objection is real
 * and it is answered, not ignored:
 *
 *   · the prefix rule produced SEVENTEEN groups for 43 keys, six of them holding exactly
 *     one key ("Sales orders", "Deployment", "Amounts", "Users", "Dashboards", "Remote
 *     access"). That is a list of headings, not a scannable picker — and the screen this
 *     feeds is the one an administrator staffs a whole team from;
 *   · the forgotten decision cannot happen: a key's group is a FIELD on a
 *     `Record<PermissionKey, PermissionMeta>` entry, so a registry key with no catalogue
 *     entry does not COMPILE. That is strictly earlier than the runtime throw the prefix
 *     rule offered, and `permission-catalog.test.ts` measures it in both directions
 *     anyway.
 *
 * What has NOT changed is the ordering contract `rbac.test.ts` pins: keys are fed in
 * `PERMISSION_KEYS` order and each group preserves it, so positions still ascend within
 * every group.
 */
export function permissionGroups(): PermissionGroup[] {
  return groupPermissions(PERMISSION_KEYS);
}
